// src/components/charts/DirectionChart.vue
<template>
  <div class="chart-container">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<style scoped>
.chart-container {
  position: relative;
  height: 100%;
  width: 100%;
  min-height: 350px;
  display: flex;
  flex-direction: column;
}
</style>

<script setup>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue'
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title, LineController, PointElement, LineElement } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

// Enregistrer les composants Chart.js nécessaires
Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title, ChartDataLabels, LineController, PointElement, LineElement)

const props = defineProps({
  data: {
    type: Array,
    required: true
  }
})

const chartCanvas = ref(null)
let chart = null

const createChart = () => {
  if (!chartCanvas.value) return
  
  // Regrouper les données par direction
  const directionCounts = {}
  props.data.forEach(p => {
    const dir = p.direction || 'Non défini'
    directionCounts[dir] = (directionCounts[dir] || 0) + 1
  })
  
  // Calculer le total pour les pourcentages
  const total = Object.values(directionCounts).reduce((sum, count) => sum + count, 0)
  
  // Préparer les données pour Chart.js
  const sortedDirections = Object.entries(directionCounts)
    .sort((a, b) => b[1] - a[1]) // Trier par nombre décroissant
    .slice(0, 8) // Limiter aux 8 principales directions pour la lisibilité
  
  const labels = sortedDirections.map(([dir]) => dir.length > 15 ? dir.slice(0, 15) + '...' : dir)
  const counts = sortedDirections.map(([, count]) => count)
  const percentages = sortedDirections.map(([, count]) => ((count / total) * 100).toFixed(1) + '%')
  
  // Palette de couleurs pour les barres
  const barColors = [
    '#42a5f5', '#66bb6a', '#ffa726', '#ab47bc', '#ef5350', '#26a69a', '#d4e157',
    '#8d6e63', '#ec407a', '#29b6f6', '#7e57c2', '#789262', '#fbc02d', '#5c6bc0',
    '#ff7043', '#9ccc65', '#26c6da', '#8d6e63', '#bdbdbd', '#dce775'
  ]
  
  const barBackgroundColors = labels.map((_, i) => barColors[i % barColors.length])
  
  // Créer le graphique
  chart = new Chart(chartCanvas.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'Nombre de pointages',
          data: counts,
          backgroundColor: barBackgroundColors,
          borderColor: barBackgroundColors,
          borderWidth: 2,
          borderRadius: 8,
          maxBarThickness: 48
        },
        {
          // Ligne de tendance
          type: 'line',
          label: 'Tendance',
          data: counts,
          backgroundColor: 'rgba(255,140,0,0.15)',
          borderColor: '#ff9800',
          borderWidth: 2,
          fill: false,
          pointBackgroundColor: '#ff9800',
          pointRadius: 4,
          pointBorderWidth: 2,
          datalabels: {
            display: false // Ne pas afficher de datalabels sur la ligne de tendance
          }
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => `${context.parsed.y} personne(s)`
          }
        },
        datalabels: {
          display: true,
          color: '#333',
          font: {
            weight: 'bold',
            size: 14
          },
          formatter: (value, context) => {
            return `${value} (${percentages[context.dataIndex]})`
          },
          anchor: 'end',
          align: 'top',
          offset: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          borderRadius: 4,
          padding: 4
        },
        title: {
          display: true,
          text: 'Répartition par direction',
          font: {
            size: 16
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Direction'
          },
          grid: {
            display: true,
            color: '#e0e0e0'
          },
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 45
          }
        },
        y: {
          title: {
            display: true,
            text: 'Nombre de personnes'
          },
          beginAtZero: true,
          precision: 0,
          grid: {
            display: true,
            color: '#e0e0e0'
          }
        }
      }
    }
  })
}

// Réagir aux changements de données
watch(() => props.data, () => {
  if (chart) {
    chart.destroy()
  }
  createChart()
}, { deep: true })

onMounted(() => {
  createChart()
})

onBeforeUnmount(() => {
  if (chart) {
    chart.destroy()
  }
})
</script>